import { dashMetaJson } from '../../../shared/api/dash-meta-fetch';
import type { ReportFlowPage } from '../components/ReportFlow/report-flow-types';
import { reconcileReportDocument } from '../report-document/reconcile-report-document';
import type { ReportDocumentConfig } from '../report-document/report-document-types';
import { deserializeItemDimensionSources } from '../utils/dimension-source-serialization';
import { serializeReportDocumentTableColumns } from '../utils/table-columns-serialization';

const REPORT_DOCUMENTS_PATH = '/api/build-a-dash/report-documents';

const deserializePage = (page: ReportFlowPage): ReportFlowPage => ({
  ...page,
  blocks: page.blocks.map((block) =>
    block.kind === 'columns'
      ? {
          kind: 'columns',
          items: block.items.map(deserializeItemDimensionSources),
        }
      : { kind: 'item', item: deserializeItemDimensionSources(block.item) }
  ),
});

export const deserializeReportDocument = (
  document: ReportDocumentConfig
): ReportDocumentConfig =>
  reconcileReportDocument(
    { ...document, pages: document.pages.map(deserializePage) },
    'load'
  );

export const getReportDocuments = async (userId?: string) => {
  const path = userId
    ? `${REPORT_DOCUMENTS_PATH}?userId=${encodeURIComponent(userId)}`
    : REPORT_DOCUMENTS_PATH;
  const documents = await dashMetaJson<ReportDocumentConfig[]>(path, {
    method: 'GET',
  });
  return documents.map(deserializeReportDocument);
};

export const getReportDocument = async (documentId: string) => {
  const document = await dashMetaJson<ReportDocumentConfig>(
    `${REPORT_DOCUMENTS_PATH}/${documentId}`,
    { method: 'GET' }
  );
  return deserializeReportDocument(document);
};

export const createReportDocument = async (document: ReportDocumentConfig) => {
  const saved = await dashMetaJson<ReportDocumentConfig>(
    REPORT_DOCUMENTS_PATH,
    {
      method: 'POST',
      body: JSON.stringify(serializeReportDocumentTableColumns(document)),
    }
  );
  return deserializeReportDocument(saved);
};

export const updateReportDocument = async (
  documentId: string,
  document: Partial<ReportDocumentConfig>
) => {
  const saved = await dashMetaJson<ReportDocumentConfig>(
    `${REPORT_DOCUMENTS_PATH}/${documentId}`,
    {
      method: 'PUT',
      body: JSON.stringify(serializeReportDocumentTableColumns(document)),
    }
  );
  return deserializeReportDocument(saved);
};

export const deleteReportDocument = (documentId: string) =>
  dashMetaJson<void>(`${REPORT_DOCUMENTS_PATH}/${documentId}`, {
    method: 'DELETE',
  });
